import { apiClient } from "@/lib/api";
import {
  isErrorResponse,
  type ErrorResponse,
  type PaginatedStandardResponse,
  type PaginationInfo,
  type StandardResponse,
} from "@/types/api-response";
import type {
  EInvoiceCreatePayload,
  EInvoiceTaxConfig,
  UpdateEInvoiceTaxConfig,
  EInvoiceRequestDetailDto,
  EInvoiceRequestListItemDto,
  GetEInvoiceRequestsParams,
} from "@/types/e-invoice-request";

// Re-export types so existing imports from this module still resolve.
export type {
  EInvoiceCreatePayload,
  EInvoiceRequestDetailDto,
  EInvoiceRequestListItemDto,
  EInvoiceRequestStatus,
  GetEInvoiceRequestsParams,
} from "@/types/e-invoice-request";

// ─── Service ──────────────────────────────────────────────────────────────────

export const adminEInvoiceService = {
  async getDefaultTaxConfig(): Promise<EInvoiceTaxConfig> {
    const response = await apiClient.get<
      StandardResponse<EInvoiceTaxConfig> | ErrorResponse
    >("/api/v1/admin/e-invoice/requests/setting");
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async updateDefaultTaxConfig(
    setting: UpdateEInvoiceTaxConfig,
  ): Promise<EInvoiceTaxConfig> {
    const response = await apiClient.post<
      StandardResponse<EInvoiceTaxConfig> | ErrorResponse
    >("/api/v1/admin/e-invoice/requests/setting", setting);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  /**
   * List e-invoice requests with optional status / tenant filter and pagination.
   * GET /api/v1/admin/e-invoice/requests
   */
  async getRequests(params: GetEInvoiceRequestsParams): Promise<{
    data: EInvoiceRequestListItemDto[];
    pagination: PaginationInfo;
  }> {
    const query: Record<string, string> = {};
    if (params.status) query.status = params.status;
    if (params.tenantId) query.tenantId = params.tenantId;
    if (params.page != null) query.page = String(params.page);
    if (params.limit != null) query.limit = String(params.limit);

    const response = await apiClient.get<
      PaginatedStandardResponse<EInvoiceRequestListItemDto> | ErrorResponse
    >("/api/v1/admin/e-invoice/requests", { params: query });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Get a single e-invoice request by ID.
   * GET /api/v1/admin/e-invoice/requests/{id}
   */
  async getRequest(id: string): Promise<EInvoiceRequestDetailDto> {
    const response = await apiClient.get<
      StandardResponse<EInvoiceRequestDetailDto> | ErrorResponse
    >(`/api/v1/admin/e-invoice/requests/${id}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
    return response.data.data;
  },

  /**
   * Adjust the payload of a request before approval.
   * POST /api/v1/admin/e-invoice/requests/{id}/adjust
   */
  async adjustRequest(
    id: string,
    payload: EInvoiceCreatePayload,
  ): Promise<EInvoiceRequestDetailDto> {
    const response = await apiClient.post<
      StandardResponse<EInvoiceRequestDetailDto> | ErrorResponse
    >(`/api/v1/admin/e-invoice/requests/${id}/adjust`, payload);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
    return response.data.data;
  },

  /**
   * Approve a request — creates a DRAFT invoice on the Hub (isSign=false).
   * POST /api/v1/admin/e-invoice/requests/{id}/approve
   */
  async approveRequest(id: string): Promise<EInvoiceRequestDetailDto> {
    const response = await apiClient.post<
      StandardResponse<EInvoiceRequestDetailDto> | ErrorResponse
    >(`/api/v1/admin/e-invoice/requests/${id}/approve`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
    return response.data.data;
  },

  /**
   * Reject a request with a mandatory reason.
   * POST /api/v1/admin/e-invoice/requests/{id}/reject
   */
  async rejectRequest(
    id: string,
    reason: string,
  ): Promise<EInvoiceRequestDetailDto> {
    const response = await apiClient.post<
      StandardResponse<EInvoiceRequestDetailDto> | ErrorResponse
    >(`/api/v1/admin/e-invoice/requests/${id}/reject`, { reason });

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }
    return response.data.data;
  },
  async createRequest(
    orderId: string,
    payload: EInvoiceCreatePayload,
  ): Promise<EInvoiceRequestDetailDto> {
    const body = {
      ...payload,
      payment: {
        ...payload.payment,
        methodCode: Number(payload.payment.methodCode),
      },
      details: payload.details.map((line) => ({
        ...line,
        chatType: Number(line.chatType || "1"),
      })),
    };
    const response = await apiClient.post<
      StandardResponse<EInvoiceRequestDetailDto> | ErrorResponse
    >(`/api/v1/admin/e-invoice/requests/${orderId}/create`, body);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },
};
